import React, { useEffect, useState } from "react";
import { Row, Col, Spinner, Image, Button } from "react-bootstrap";
import { get } from "../API/api_helper";
import Swal from "sweetalert2";
import CompanyModal from "../Modals/CompanyModal";

interface Company {
  id: number;
  name: string;
  email?: string;
  phone_code?: string;
  phone?: string;
  address1?: string;
  address2?: string;
  city?: string;
  street?: string;
  pincode?: string;
  state?: string;
  country?: string;
  website?: string;
  logo?: string | null;
}

const CompanyView: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await get("/organizations");

      let companiesData = [];

      if (Array.isArray(response?.data)) {
        companiesData = response.data;
      } else if (Array.isArray(response)) {
        companiesData = response;
      } else if (response?.data?.data) {
        companiesData = response.data.data;
      } else if (response?.data?.id) {
        companiesData = [response.data];
      } else if (response?.id) {
        companiesData = [response];
      }

      setCompanies(companiesData);
    } catch (error: any) {
      console.error("Error fetching companies:", error);
      Swal.fire({
        icon: "error",
        title: "Failed to fetch company data",
        text: error.response?.data?.message || "Please try again later.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
        <p>Loading company details...</p>
      </div>
    );
  }

  if (companies.length === 0) {
    return (
      <div className="text-center mt-5">
        <h5>No company details found.</h5>
      </div>
    );
  }

  const company = companies[0];

  return (
    <>
      <div
        className="p-3 p-md-4 mx-auto"
        style={{
          maxWidth: "1300px",
          width: "100%",
          backgroundColor: "#ffffff",
          borderRadius: "10px",
        }}
      >
        <h3
          style={{
            borderBottom: "1px solid",
            display: "inline-block",
            paddingBottom: "5px",
            marginBottom: "20px",
          }}
        >
          Company Details
        </h3>

        {/* Logo + Name Section */}
        <div
          className="d-flex flex-column flex-sm-row align-items-center text-center text-sm-start mb-4"
        >
          {company.logo ? (
            <Image
              src={company.logo}
              alt={company.name}
              roundedCircle
              style={{
                width: "120px",
                height: "120px",
                objectFit: "cover",
                boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
              }}
            />
          ) : (
            <div
              style={{
                width: "120px",
                height: "120px",
                borderRadius: "50%",
                backgroundColor: "#e9ecef",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "2rem",
                color: "#6c757d",
                boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
              }}
            >
              {company.name.charAt(0).toUpperCase()}
            </div>
          )}

          <h3
            className="mt-3 mt-sm-0 ms-sm-4"
            style={{
              color: "#007bff",
              fontWeight: 600,
              letterSpacing: "0.5px",
              wordBreak: "break-word",
            }}
          >
            {company.name.toUpperCase()}
          </h3>
        </div>

        {/* Info Section */}
        <Row className="gy-3" style={{ marginTop: "20px", marginBottom: "20px" }}>
          <Col xs={12} md={6}>
            <p>
              <strong>Email:</strong> {company.email || "N/A"}
            </p>
            <p>
              <strong>Phone:</strong> {company.phone_code} {company.phone}
            </p>
            <p>
              <strong>Website:</strong>{" "}
              {company.website ? (
                <a
                  href={company.website}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: "#0d6efd", textDecoration: "none" }}
                >
                  {company.website}
                </a>
              ) : (
                "N/A"
              )}
            </p>
          </Col>

          <Col xs={12} md={6}>
            <p>
              <strong>City:</strong> {company.city || "N/A"}
            </p>
            <p>
              <strong>State:</strong> {company.state || "N/A"}
            </p>
            <p>
              <strong>Country:</strong> {company.country || "N/A"}
            </p>
          </Col>
        </Row>

        {/* Address */}
        <p style={{ lineHeight: 1.6, wordBreak: "break-word" }}>
          <strong>Address:</strong>{" "}
          {[company.address1, company.address2, company.street, company.pincode]
            .filter(Boolean)
            .join(", ") || "N/A"}
        </p>

        <hr style={{ margin: "20px 0" }} />

        {/* Edit Button */}
        <div className="text-center mt-3 mb-4">
          <Button
            variant="primary"
            size="sm"
            style={{
              borderRadius: "30px",
              padding: "8px 25px",
              boxShadow: "0 3px 10px rgba(0,123,255,0.3)",
              fontWeight: 500,
            }}
            onClick={() => {
              setSelectedCompany(company);
              setShowModal(true);
            }}
          >
            Edit
          </Button>
        </div>
      </div>

      <CompanyModal
        show={showModal}
        onHide={() => setShowModal(false)}
        test="Edit"
        company={selectedCompany}
      />
    </>
  );
};

export default CompanyView;
